import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSidebar } from "../app/App";
import { getClubs } from "../services/api";
import { createOwnClub } from "../services/clubs";
import DemoBanner from "../components/DemoBanner";
import { ClubNavIcon, CLUB_NAV_LABELS } from "./clubNav";
import ClubTopbar from "./ClubTopbar";
import { useOverlayScrollbar } from "../hooks/useOverlayScrollbar";

interface ClubAdminLayoutProps {
  children: React.ReactNode;
}

type NavItem = {
  id: string;
  path: string;
  label: string;
  icon: React.ReactNode;
};

export default function ClubAdminLayout({ children }: ClubAdminLayoutProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeClubId, setActiveClubId, refresh, coordinatorsEnabled } = useAuth();
  const {
    clubAdminMenuOpen: mobileMenuOpen,
    setClubAdminMenuOpen: setMobileMenuOpen,
    setClubPanelMounted,
  } = useSidebar();

  // Tell the shell we are here, so it hides its own header. Without this a
  // club admin on a page outside /dashboard/admin-club — their own profile —
  // got two stacked headers.
  useEffect(() => {
    setClubPanelMounted(true);
    return () => setClubPanelMounted(false);
  }, [setClubPanelMounted]);

  // The menu is the only part of the sidebar that scrolls: the club selector
  // has to stay level with the header and the support card has to stay
  // reachable, so neither of them may scroll away.
  const navScrollRef = useRef<HTMLDivElement>(null);
  const navThumb = useOverlayScrollbar(navScrollRef);

  const [clubs, setClubs] = useState<{ role: string; club: { id: string; name: string; plan_code?: string } | null }[]>([]);
  const [loadingClubs, setLoadingClubs] = useState(true);
  // Add-club (Premium self-serve)
  const [showAddClub, setShowAddClub] = useState(false);
  const [addClubName, setAddClubName] = useState("");
  const [addingClub, setAddingClub] = useState(false);
  const [addClubError, setAddClubError] = useState("");

  const canAddClubs = clubs.some((c) => c.club && (c.club as any).plan_code === "premium");

  const handleAddClub = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addClubName.trim()) { setAddClubError(t("clubAdmin.addClubNameRequired", "Numele clubului este obligatoriu.")); return; }
    setAddingClub(true);
    setAddClubError("");
    try {
      const club = await createOwnClub({ name: addClubName.trim() });
      // Reload the club list (context + local) and switch to the new club.
      const c = await getClubs();
      setClubs(c);
      setActiveClubId(club.id);
      await refresh();
      setShowAddClub(false);
      setAddClubName("");
    } catch (err: any) {
      setAddClubError(err?.response?.data?.error || t("clubAdmin.addClubFailed", "Nu am putut crea clubul."));
    } finally {
      setAddingClub(false);
    }
  };

  // Close the mobile menu whenever the route changes — clicking a nav link
  // should dismiss the slide-in overlay automatically.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, setMobileMenuOpen]);

  // Load clubs on mount
  useEffect(() => {
    (async () => {
      try {
        const c = await getClubs();
        setClubs(c);
        // If no active club, select the first one
        if (!activeClubId) {
          const firstClub = c.find(x => x.club)?.club?.id || null;
          if (firstClub) setActiveClubId(firstClub);
        }
      } catch (error) {
        console.error("Failed to load clubs:", error);
      }
      setLoadingClubs(false);
    })();
  }, [activeClubId, setActiveClubId]);

  const clubOptions = clubs.filter(c => c.club).map(c => c.club!);

  const navItems: NavItem[] = [
    {
      id: "overview",
      path: "/dashboard/admin-club",
      label: t("admin.overview", "Overview"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      ),
    },
    {
      id: "details",
      path: "/dashboard/admin-club/details",
      label: t("admin.details", "Details"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
    },
    {
      id: "teams",
      path: "/dashboard/admin-club/teams",
      label: t("admin.teams", "Teams"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      id: "coaches",
      path: "/dashboard/admin-club/coaches",
      label: t("admin.coaches", "Coaches"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
    },
    {
      id: "staff",
      path: "/dashboard/admin-club/staff",
      label: t("admin.staff", "Coordonatori"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
    },
    {
      id: "athletes",
      path: "/dashboard/admin-club/athletes",
      label: t("admin.athletes", "Athletes"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
    {
      id: "quizzes",
      path: "/dashboard/admin-club/quizzes",
      label: t("admin.quizzes", "Quiz-uri"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
    },
    {
      id: "products",
      path: "/dashboard/admin-club/products",
      label: t("admin.products", "Products"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
    },
    {
      id: "reports",
      path: "/dashboard/admin-club/reports",
      label: t("admin.reports", "Rapoarte"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6m4 6V7m4 10v-4M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      id: "receivables",
      path: "/dashboard/admin-club/receivables",
      label: t("admin.receivables", "De încasat"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      id: "events",
      path: "/dashboard/admin-club/events",
      label: t("admin.events", "Events"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          <circle cx="8" cy="14" r="1" fill="currentColor" />
          <circle cx="12" cy="14" r="1" fill="currentColor" />
          <circle cx="16" cy="14" r="1" fill="currentColor" />
        </svg>
      ),
    },
    {
      id: "sessions",
      path: "/dashboard/admin-club/sessions",
      label: t("admin.sessions", "Sessions"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      id: "invoices",
      path: "/dashboard/admin-club/invoices",
      label: t("admin.invoices", "Invoices"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      id: "locations",
      path: "/dashboard/admin-club/locations",
      label: t("admin.locations", "Locations"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      id: "calendar",
      path: "/dashboard/admin-club/calendar",
      label: t("admin.calendar", "Calendar"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11h8v8H8z" />
        </svg>
      ),
    },
    {
      id: "leaderboard",
      path: "/dashboard/admin-club/leaderboard",
      label: t("admin.leaderboard", "Leaderboard"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      id: "affiliate",
      path: "/dashboard/admin-club/affiliate",
      label: t("admin.affiliate", "Afiliere"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.8 10.2a4 4 0 00-5.7 0l-3 3a4 4 0 105.7 5.7l1.5-1.5m-1.1-4.6a4 4 0 005.7 0l3-3a4 4 0 10-5.7-5.7l-1.5 1.5" />
        </svg>
      ),
    },
    {
      id: "settings",
      path: "/dashboard/admin-club/settings",
      label: t("admin.settings", "Setări"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.3 4.3a2 2 0 013.4 0l.5.9a2 2 0 002.3 1l1-.3a2 2 0 012.4 2.4l-.3 1a2 2 0 001 2.3l.9.5a2 2 0 010 3.4l-.9.5a2 2 0 00-1 2.3l.3 1a2 2 0 01-2.4 2.4l-1-.3a2 2 0 00-2.3 1l-.5.9a2 2 0 01-3.4 0l-.5-.9a2 2 0 00-2.3-1l-1 .3a2 2 0 01-2.4-2.4l.3-1a2 2 0 00-1-2.3l-.9-.5a2 2 0 010-3.4l.9-.5a2 2 0 001-2.3l-.3-1A2 2 0 016.8 5.9l1 .3a2 2 0 002.3-1l.5-.9z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      ),
    },
  ];

  // Grouped navigation: 16 flat items → labelled sections. Labels are forced to
  // Romanian here (the per-item t() fallbacks were partly English). All routes
  // are preserved; only presentation changes.
  const NAV_LABELS = CLUB_NAV_LABELS;
  const NAV_GROUPS: { label: string | null; ids: string[] }[] = [
    { label: null, ids: ["overview"] },
    { label: "Oameni", ids: ["teams", "coaches", "staff", "athletes"] },
    { label: "Program", ids: ["sessions", "calendar", "locations", "events", "reports"] },
    // Numerarul antrenorilor a intrat ca tab in "De încasat": e aceeasi
    // intrebare, pusa de aceeasi persoana, in acelasi minut.
    { label: "Finanțe", ids: ["receivables", "invoices", "products", "affiliate"] },
    // Clasament + Setări sit ungrouped as the last items. Quiz-uri is no longer
    // exposed in the club menu, Detalii was merged into Setări, and Suport is
    // the card pinned at the bottom of the sidebar — one entry point, not two.
    { label: null, ids: ["leaderboard", "settings"] },
  ];
  // Coordinators are an opt-in for large clubs. Until a club ticks the box in
  // Setari the page stays out of the menu entirely, rather than sitting there
  // as a section nobody in that club will ever use.
  const visibleNavItems = navItems.filter((n) => n.id !== "staff" || coordinatorsEnabled);
  const byId = Object.fromEntries(visibleNavItems.map((n) => [n.id, n]));

  // Check if a nav item is active
  const isActive = (item: NavItem) => {
    // Exact match for overview (base path)
    if (item.id === "overview") {
      return location.pathname === "/dashboard/admin-club";
    }
    // For other items, check if path starts with item path
    return location.pathname.startsWith(item.path);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar state is shared via SidebarContext (clubAdminMenuOpen). */}

      {/* Backdrop for mobile slide-in sidebar */}
      {mobileMenuOpen && (
        <div
          onClick={() => setMobileMenuOpen(false)}
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          aria-hidden="true"
        />
      )}

      <div className="flex">
        {/* Left Sidebar
            A full-height column pinned to the viewport: the club selector at
            the top, the menu scrolling in the middle, the support card at the
            bottom. The dark global header from FrontendLayout stands down on
            these routes (ClubTopbar replaces it), so the aside starts at
            top-0 and its first strip lines up with that top bar. Mobile keeps
            `fixed` because the drawer overlays the whole viewport. */}
        <aside
          className={`
            fixed lg:sticky top-0 left-0 z-50 flex flex-col flex-shrink-0 w-64 h-[100dvh] max-h-[100dvh]
            overflow-hidden bg-white
            transform transition-transform duration-200 ease-out
            lg:translate-x-0
            ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          `}
        >
          {/* Mobile-only close button at top-right of the sidebar */}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden absolute top-2.5 right-3 z-10 p-1.5 rounded-lg hover:bg-slate-700 text-slate-300"
            aria-label={t("common.close_menu", "Închide meniul")}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Club Selector — the dark strip that sits level with <header>.
              Both are h-14 (56px) and both are the first thing in their
              column, so the selector and the top bar line up exactly. It is
              flex-shrink-0 and outside the scroll area below, so scrolling the
              menu can never slide it out of that alignment. */}
          <div className="flex items-center flex-shrink-0 h-14 px-3 bg-slate-900">
            <select
              value={activeClubId || ""}
              onChange={e => setActiveClubId(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-lg bg-slate-800 border border-slate-700 text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
              disabled={loadingClubs}
            >
              <option value="" disabled>— {t("common.select", "Select")} —</option>
              {clubOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {/* Premium-only: add another club */}
            {canAddClubs && (
              <button
                type="button"
                onClick={() => { setAddClubError(""); setShowAddClub(true); }}
                title={t("clubAdmin.addClub", "Adaugă club")}
                className="ml-2 shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-lg font-bold"
              >
                +
              </button>
            )}
          </div>

          {/* Navigation Menu — grouped into labelled sections.
              The scroll lives here rather than on the whole aside, so the club
              selector above and the support card below stay put. `min-h-0` is
              what lets a flex child actually shrink and scroll instead of
              growing its parent. The thumb is drawn by useOverlayScrollbar:
              transparent, over the content, and only while scrolling. */}
          <div
            ref={navScrollRef}
            className="relative flex-1 min-h-0 overflow-y-auto overlay-scroll border-r border-slate-200"
          >
            {navThumb && (
              <div
                aria-hidden="true"
                className="absolute right-1 w-1.5 rounded-full pointer-events-none bg-slate-400/60 transition-opacity duration-300"
                style={{
                  top: navThumb.top,
                  height: navThumb.height,
                  opacity: navThumb.visible ? 1 : 0,
                }}
              />
            )}
            <nav className="p-3 space-y-2.5">
            {NAV_GROUPS.map((group) => (
              <div key={group.label ?? "top"} className="space-y-0.5">
                {group.label && (
                  <p className="px-3 pt-0.5 pb-0.5 text-[10.5px] font-bold uppercase tracking-[0.09em] text-slate-400">{group.label}</p>
                )}
                {group.ids.map((id) => {
                  const item = byId[id];
                  if (!item) return null;
                  const active = isActive(item);
                  return (
                    <button
                      key={item.id}
                      onClick={() => navigate(item.path)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        active
                          ? "bg-indigo-50 text-indigo-700 border border-indigo-200"
                          : "text-slate-600 hover:text-slate-800 hover:bg-slate-50"
                      }`}
                    >
                      <span className={active ? "text-indigo-600" : "text-slate-400"}><ClubNavIcon id={item.id} /></span>
                      <span>{NAV_LABELS[item.id] ?? item.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
            </nav>
          </div>

          {/* Support.
              Was a phone number and a mailbox, both of which pointed at
              placeholders nobody answered. One button into the support page
              instead, where the request is recorded and someone sees it. */}
          <div className="flex-shrink-0 p-4 border-t border-r border-slate-200 bg-gradient-to-br from-indigo-50 to-violet-50">
            <Link
              to="/dashboard/admin-club/support"
              className="flex items-center gap-3 p-3 transition-shadow bg-white border shadow-sm group rounded-xl border-indigo-100 hover:shadow-md hover:border-indigo-200"
            >
              <span className="flex items-center justify-center flex-shrink-0 w-10 h-10 text-white shadow-sm rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m2.829 2.829L18 18m-6-6a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-bold text-slate-800 group-hover:text-indigo-700">
                  {t("admin.support", "Suport")}
                </span>
                <span className="block text-xs text-slate-500">
                  {t("admin.supportHint", "Scrie-ne, îți răspundem")}
                </span>
              </span>
              <svg
                className="w-4 h-4 ml-auto transition-transform flex-shrink-0 text-slate-400 group-hover:translate-x-0.5 group-hover:text-indigo-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </aside>

        {/* Main Content
            The sticky <header> in FrontendLayout already pins itself
            via its own `sticky top-0 z-50` — main does NOT need a
            z-index of its own. Adding `relative z-40` here creates a
            stacking context that traps any `fixed` modal rendered
            inside main beneath the header, so we deliberately leave
            main as static flow content. */}
        <main className="flex-1 min-h-screen w-full lg:w-auto min-w-0">
          {/* Club-scoped light top bar spans only the main column, so the
              sidebar starts at the very top. */}
          <ClubTopbar />
          <div className="p-4 lg:p-6">
            <DemoBanner />
            {children}
          </div>
          {/* EU co-financing note — small, grey, no background. */}
          <footer className="px-4 lg:px-6 pb-6 pt-2 text-center text-[11px] leading-relaxed text-slate-400">
            <p>
              Această platformă web și aplicație mobilă au fost realizate cu sprijinul financiar al Uniunii Europene prin Programul Educației și Ocupare 2021-2027, cofinanțat din FSE+.<br />
              Conținutul acestui material nu reprezintă în mod obligatoriu poziția oficială a Uniunii Europene sau a Guvernului României.<br />
              Pentru informații detaliate despre celalalte programe finanțate de Uniunea Europeană, vă rugăm să vizitați{" "}
              <a href="https://www.fonduri-ue.ro" target="_blank" rel="nofollow" className="underline hover:text-slate-600">www.fonduri-ue.ro</a>
            </p>
          </footer>
        </main>
      </div>

      {/* Add-club modal (Premium) */}
      {showAddClub && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full border border-slate-200 overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-800">{t("clubAdmin.addClub", "Adaugă club")}</h2>
              <button onClick={() => setShowAddClub(false)} className="w-7 h-7 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200">✕</button>
            </div>
            <form onSubmit={handleAddClub} className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">{t("clubAdmin.clubName", "Numele clubului")}</label>
                <input
                  autoFocus
                  value={addClubName}
                  onChange={(e) => setAddClubName(e.target.value)}
                  placeholder={t("clubAdmin.clubNamePlaceholder", "Ex: CS Viitorul")}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <p className="text-xs text-slate-400">{t("clubAdmin.addClubHint", "Vei putea completa restul detaliilor din setările clubului.")}</p>
              {addClubError && <p className="text-xs text-rose-600">{addClubError}</p>}
              <div className="flex gap-2 justify-end pt-1">
                <button type="button" onClick={() => setShowAddClub(false)} className="px-4 py-2 text-sm font-semibold rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200">
                  {t("common.cancel", "Anulează")}
                </button>
                <button type="submit" disabled={addingClub} className="px-4 py-2 text-sm font-bold rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-60">
                  {addingClub ? t("common.saving", "Se salvează...") : t("clubAdmin.createClub", "Creează")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
